import { useState } from "react";
import { toast } from "sonner";
import { Download, Sparkles } from "lucide-react";
import { http, imageUrl, notify } from "@/lib/api";

const styles = ["Photo réaliste", "Cinématique", "Aquarelle", "Vintage", "Illustration", "Cyberpunk"];
const presets = ["Un café parisien au lever du soleil, ambiance douce", "Un chat roux lisant un livre près d'une fenêtre", "Un paysage de Provence avec des champs de lavande"];

export default function ImagePanel({ onDone }) {
  const [prompt, setPrompt] = useState("");
  const [style, setStyle] = useState(styles[0]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    setBusy(true);
    setResult(null);
    toast("Sem.IA crée votre image…");
    try {
      const r = await http.post("/images", { prompt, style });
      setResult(r.data);
      toast.success("Image prête ! Ajoutée à votre galerie.");
      notify("Sem.IA", "Votre image est prête !");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la génération");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="card p-6 sm:p-8 space-y-5">
        <h2 className="font-serif text-3xl">Créer une image</h2>
        <textarea data-testid="nano-banana-prompt-input" rows={5} className="field resize-none" placeholder="Décrivez l'image que vous imaginez…" value={prompt} onChange={(e) => setPrompt(e.target.value)} />
        <div className="flex flex-wrap gap-2">
          {presets.map((p, i) => (
            <button key={p} data-testid={`image-preset-${i}`} onClick={() => setPrompt(p)} className="text-xs px-3 py-1.5 rounded-full bg-[var(--surface-subtle)] text-[var(--text-secondary)] hover:bg-[var(--text)] hover:text-[var(--bg)] transition-colors">{p}</button>
          ))}
        </div>
        <div>
          <span className="label-mono text-[var(--text-muted)]">Style</span>
          <div className="flex flex-wrap gap-2 mt-2">
            {styles.map((s) => (
              <button key={s} data-testid={`image-style-${s}`} onClick={() => setStyle(s)} className={`text-sm px-4 py-2 rounded-full border transition-colors ${style === s ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "border-[var(--border)] hover:border-[var(--accent)]"}`}>{s}</button>
            ))}
          </div>
        </div>
        <button data-testid="nano-banana-generate-button" disabled={!prompt.trim() || busy} onClick={generate} className="btn-primary w-full">
          <Sparkles size={16} /> {busy ? "Création en cours…" : "Générer l'image"}
        </button>
      </div>
      <div className="card p-4 flex items-center justify-center min-h-[360px] overflow-hidden">
        {busy && <div className="w-full aspect-square rounded-2xl bg-[var(--surface-subtle)] animate-pulse flex items-center justify-center text-[var(--text-muted)]">Sem.IA peint…</div>}
        {!busy && result && (
          <div className="w-full space-y-3">
            <img data-testid="generated-image" src={imageUrl(result.id)} alt={result.prompt} className="w-full rounded-2xl" />
            <div className="flex gap-2">
              <a data-testid="generated-image-download" href={imageUrl(result.id)} download={`lea-${result.id}.png`} className="btn-ghost flex-1"><Download size={16} /> Télécharger</a>
              <button data-testid="generated-image-gallery" onClick={onDone} className="btn-primary flex-1">Voir la galerie</button>
            </div>
          </div>
        )}
        {!busy && !result && <p className="text-[var(--text-muted)] text-center">Votre création apparaîtra ici.</p>}
      </div>
    </div>
  );
}
